import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';

// Local utilities and configurations
import { db, initializeAndFetchAllTables } from './Utils/Functions/Dexie-Setup';
import { getCookieValue, pFetch, ticketCookieName, parseXml } from './Utils/Functions/helpers';
import { quickbaseFetch } from './Utils/Functions/QbAuthedFetch';
import { appId } from './Utils/Functions/Table_Configs';
import { translations } from './Utils/Maps/translations';

// --- Constants and Helper Functions ---
const StoreContext = createContext(null);

const getLang = () => (navigator.languages?.length ? navigator.languages[0] : navigator.language);

const getInitialState = (key, defaultValue, transform = (val) => val) => {
  try {
    const storedValue = window.localStorage.getItem(key);
    return storedValue !== null ? transform(storedValue) : defaultValue;
  } catch (e) {
    console.error(`Error reading from localStorage: ${key}`, e);
    return defaultValue;
  }
};

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Unique ID for the current browser tab to handle cross-tab race conditions
const tabUid = `${Date.now()}-${Math.random()}`;


// --- Provider Component ---
export const StoreProvider = ({ children, iframeSrc, userAgent }) => {
  // --- State Management ---
  // Use lazy initialization to read from localStorage only once
  const [isOnline, setIsOnline] = useState(() => window.navigator.onLine);
  const [isAuthed, setIsAuthed] = useState(() => !!getCookieValue(ticketCookieName));
  const [language, stateSetLanguage] = useState(() => getInitialState("language", getLang().includes("es") ? "es" : "en"));
  const [hours, stateSetHours] = useState(() => getInitialState("hours", 72, Number));
  const [settingsVisited, stateSetSettingsVisited] = useState(() => getInitialState("settingsVisited", false, (val) => val === "true"));
  const [translation, setTranslation] = useState(() => translations[language] || {});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const debounce = useRef();
  const pendingSubmissions = useLiveQuery(() => db.dailyFieldReports.where("recordId").below(0).and(dfr => !!dfr?.submitted).primaryKeys(), []);

  // --- Memoized API and Data Logic ---
  const dataApi = useMemo(() => {
    return initializeAndFetchAllTables(new URL(iframeSrc).origin, userAgent, quickbaseFetch);
  }, [iframeSrc, userAgent]);

  const signIn = useCallback(async (username, password, ticket, manualHours) => {
    const authPayload = ticket ? `<ticket>${ticket}</ticket>` : `<username>${username}</username><password>${password}</password>`;
    try {
      const response = await pFetch(`https://lighthouseelectric.quickbase.com/db/main`, {
        method: "POST",
        headers: { "Content-Type": "application/xml", "QUICKBASE-ACTION": "API_Authenticate" },
        body: `<qdbapi>${authPayload}<hours>${manualHours ?? hours}</hours></qdbapi>`
      });
      const resText = await response.text();
      const resObj = parseXml(resText);
      const newTicket = resObj?.qdbapi?.ticket?.["#text"];

      if (response.ok && newTicket) {
        const maxAge = manualHours ?? hours ? `; max-age=${Math.ceil(hours * 3600)}` : "";
        document.cookie = `${ticketCookieName}=${newTicket}${maxAge}; path=/`;
        setIsAuthed(true);
        return { ok: true, parsedBody: resObj };
      }
      setIsAuthed(false);
      return { ok: false, parsedBody: resObj };
    } catch (error) {
      console.error("Sign-in failed:", error);
      setIsAuthed(false);
      return { ok: false, error };
    }
  }, [hours]);

  const signOut = useCallback(() => {
    document.cookie = `${ticketCookieName}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/;`;
    clearTimeout(debounce.current);
    setIsAuthed(false);
    dataApi.clearFetchedData();
  }, [dataApi]);

  // --- State Setters with Side Effects ---
  const setLanguage = useCallback((value) => {
    stateSetLanguage(value);
    window.localStorage.setItem("language", value);
  }, []);

  const setHours = useCallback((value) => {
    const newHours = Number(value) || 0;
    stateSetHours(newHours);
    window.localStorage.setItem("hours", newHours);

    const ticket = getCookieValue(ticketCookieName);
    if (ticket && isOnline) {
      clearTimeout(debounce.current);
      debounce.current = setTimeout(() => signIn("", "", ticket, newHours), 10000);
    }
  }, [isOnline, signIn]);

  const setSettingsVisited = useCallback((value) => {
    //  FIX: Was calling stateSetLanguage, now correctly calls stateSetSettingsVisited
    stateSetSettingsVisited(value);
    window.localStorage.setItem("settingsVisited", String(value));
  }, []);

  // --- Side Effects ---
  // Handles online/offline status changes
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Handles periodic data sync and ticket refresh
  useEffect(() => {
    if (!isOnline || !isAuthed) return;
    dataApi.syncIfTimePassed();
    const ticket = getCookieValue(ticketCookieName);
    if (ticket) signIn("", "", ticket); // Refresh ticket

    const intervalId = setInterval(() => dataApi.syncIfTimePassed(), 1000 * 60 * 15); // Sync every 15 min
    return () => clearInterval(intervalId);
  }, [isOnline, isAuthed, dataApi, signIn]);

  // Handles submitting pending records created while offline
  useEffect(() => {
    const canSubmit = isOnline && isAuthed && pendingSubmissions?.length > 0;
    const submittingLock = window.localStorage.getItem(`${appId}_IsSubmitting`);
    const isAnotherTabSubmitting = submittingLock && submittingLock !== "false";

    if (!canSubmit || isAnotherTabSubmitting) return;

    const submitData = async () => {
      // Use a cross-tab lock to prevent duplicate submissions
      window.localStorage.setItem(`${appId}_IsSubmitting`, tabUid);
      await delay(10); // Brief delay to let other tabs see the lock

      // If another tab took over, abort this attempt
      if (window.localStorage.getItem(`${appId}_IsSubmitting`) !== tabUid) return;

      setIsSubmitting(true);
      const beforeUnloadHandler = (e) => (e.preventDefault(), e.returnValue = '');
      window.addEventListener("beforeunload", beforeUnloadHandler);

      try {
        const pendingIds = await db.dailyFieldReports.where("recordId").below(0).and(dfr => !!dfr?.submitted).primaryKeys();
        await dataApi.postRecords("dailyFieldReports", pendingIds, 1);
      } catch (error) {
        console.error("Failed to post pending records:", error);
      } finally {
        window.removeEventListener("beforeunload", beforeUnloadHandler);
        window.localStorage.setItem(`${appId}_IsSubmitting`, "false"); // Release lock
        setIsSubmitting(false);
      }
    };
    submitData();
  }, [isOnline, isAuthed, pendingSubmissions, dataApi]);

  // Updates translation when language changes
  useEffect(() => setTranslation(translations[language] || {}), [language]);

  // --- Context Value ---
  const contextValue = useMemo(() => ({
    isAuthed,
    isOnline,
    isSubmitting,
    signIn,
    signOut,
    translation,
    language,
    isSpanish: language === "es",
    hours,
    setLanguage,
    setHours,
    setSettingsVisited,
    ...dataApi,
  }), [isAuthed, isOnline, isSubmitting, signIn, signOut, translation, language, hours, setLanguage, setHours, setSettingsVisited, dataApi]);

  return (
    <StoreContext.Provider value={contextValue}>
      {children}
    </StoreContext.Provider>
  );
};

// --- Custom Hook ---
export const useStore = () => {
  const context = useContext(StoreContext);
  if (context === null) {
    throw new Error("useStore must be used within a StoreProvider");
  }
  return context;
};
